'use client';

import React, { useRef, useEffect, forwardRef, useState } from 'react';
import Hls from 'hls.js';
import { useTranslation } from 'react-i18next';
import { getStaticAssetUrl } from '@/app/lib/utils/static-asset-url';
import useViewportActivation from '@/app/lib/hooks/use-viewport-activation';

const VIDEO_POSTERS = [
    getStaticAssetUrl('/home/img-video-data-cover-1.png'),
    getStaticAssetUrl('/home/img-video-data-cover-2.png'),
    getStaticAssetUrl('/home/img-video-data-cover-3.png'),
];

const HlsVideoPlayer = forwardRef<
    HTMLVideoElement,
    { poster: string; className?: string }
>(({ poster, className }, ref) => {
    const handleContextMenu = (event: React.MouseEvent) => {
        event.preventDefault();
    };

    return (
        <video
            ref={ref}
            playsInline
            muted
            loop
            poster={poster}
            className={className}
            onContextMenu={handleContextMenu}
        />
    );
});
HlsVideoPlayer.displayName = 'HlsVideoPlayer';

const VideoDataCard = forwardRef<
    HTMLVideoElement,
    {
        poster: string;
        className: string;
        videoClassName?: string;
    }
>(({ poster, className, videoClassName }, ref) => {
    const [posterLoaded, setPosterLoaded] = useState(false);

    useEffect(() => {
        setPosterLoaded(false);

        const image = new window.Image();
        image.src = poster;

        if (image.complete && image.naturalWidth > 0) {
            setPosterLoaded(true);
            return;
        }

        const handleLoad = () => setPosterLoaded(true);
        const handleError = () => setPosterLoaded(true);

        image.addEventListener('load', handleLoad);
        image.addEventListener('error', handleError);

        return () => {
            image.removeEventListener('load', handleLoad);
            image.removeEventListener('error', handleError);
        };
    }, [poster]);

    return (
        <div className={className}>
            {!posterLoaded && (
                <div className="absolute inset-0 z-10 overflow-hidden rounded-[16px] bg-white/5 before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_1.6s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/10 before:to-transparent" />
            )}
            <HlsVideoPlayer
                ref={ref}
                poster={poster}
                className={
                    posterLoaded
                        ? (videoClassName ?? 'h-full w-full object-cover')
                        : `${videoClassName ?? 'h-full w-full object-cover'} opacity-0`
                }
            />
        </div>
    );
});
VideoDataCard.displayName = 'VideoDataCard';

export default function VideoData() {
    const { t } = useTranslation('video-data');

    const videoRef0 = useRef<HTMLVideoElement>(null);
    const videoRef1 = useRef<HTMLVideoElement>(null);
    const videoRef2 = useRef<HTMLVideoElement>(null);
    const { ref: activationRef, isActive: shouldLoadVideos } =
        useViewportActivation<HTMLElement>({
            rootMargin: '400px 0px',
        });

    const SRCS = [
        `${process.env.NEXT_PUBLIC_VIDEO_BASE_URL}/home/video-video-data-1-hls/index.m3u8`,
        `${process.env.NEXT_PUBLIC_VIDEO_BASE_URL}/home/video-video-data-2-hls/index.m3u8`,
        `${process.env.NEXT_PUBLIC_VIDEO_BASE_URL}/home/video-video-data-3-hls/index.m3u8`,
    ];

    useEffect(() => {
        const videoRefs = [videoRef0, videoRef1, videoRef2];
        const hlsList: Hls[] = [];

        if (!shouldLoadVideos) {
            videoRefs.forEach((ref) => {
                const video = ref.current;
                if (!video) return;
                video.pause();
                video.removeAttribute('src');
                video.load();
            });
            return;
        }

        SRCS.forEach((src, i) => {
            const video = videoRefs[i].current;
            if (!video) return;

            const playVideo = () => {
                video.play().catch(() => {});
            };

            if (video.canPlayType('application/vnd.apple.mpegurl')) {
                video.src = src;
                video.addEventListener('loadedmetadata', playVideo, {
                    once: true,
                });
            } else if (Hls.isSupported()) {
                const hls = new Hls();
                hlsList.push(hls);
                hls.loadSource(src);
                hls.attachMedia(video);
                hls.on(Hls.Events.MANIFEST_PARSED, playVideo);
            }
        });

        return () => {
            hlsList.forEach((hls) => hls.destroy());
            videoRefs.forEach((ref) => {
                const v = ref.current;
                if (!v) return;
                v.pause();
                v.removeAttribute('src');
                v.load();
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldLoadVideos]);

    const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
        const el = e.currentTarget;
        const isAtTop = el.scrollTop <= 0;
        const isAtBottom =
            Math.abs(el.scrollHeight - el.scrollTop - el.clientHeight) <= 1;

        if (isAtTop && e.deltaY < 0) return;
        if (isAtBottom && e.deltaY > 0) return;

        e.stopPropagation();
    };

    return (
        <section
            ref={activationRef}
            className="tablet:pt-[120px] desktop:pt-[160px] tablet:pb-[80px] desktop:pb-[100px] relative z-10 flex w-full flex-col items-center bg-black pb-[60px] pt-[80px] text-white"
        >
            <div className="tablet:max-w-[87.5vw] desktop:max-w-[1160px] tablet:gap-[32px] desktop:gap-[48px] flex w-[87.5vw] max-w-[480px] flex-col gap-[24px]">
                <div className="tablet:text-center j-h4 tablet:j-h3 w-full text-start">
                    <p>{t('title.part1')}</p>
                    <p>
                        <span className="bg-[linear-gradient(90deg,#F7A1FF,#7AAFFF)] bg-clip-text text-transparent">
                            {t('title.part2')}
                        </span>
                    </p>
                </div>

                <div className="tablet:gap-[8px] relative flex w-full flex-col gap-[4px]">
                    {/* Images Layout Container */}
                    <div className="tablet:flex-row tablet:gap-[8px] relative flex w-full flex-col gap-[4px]">
                        {/* Left Original Image (Desktop) / Top (Mobile) */}
                        <VideoDataCard
                            ref={videoRef0}
                            poster={VIDEO_POSTERS[0]}
                            className="tablet:w-2/3 aspect-[16:10] relative w-full overflow-hidden rounded-[4px] border border-white/10 bg-white/5"
                            videoClassName="w-full h-full object-cover"
                        />

                        {/* Right Info Images (Desktop) / Middle (Mobile) */}
                        <div className="tablet:flex-col tablet:w-1/3 tablet:gap-[8px] flex h-auto w-full flex-row gap-[4px]">
                            <VideoDataCard
                                ref={videoRef1}
                                poster={VIDEO_POSTERS[1]}
                                className="tablet:w-full aspect-[16:10] tablet:flex-1 relative w-1/2 overflow-hidden rounded-[4px] border border-white/10 bg-white/5"
                                videoClassName="w-full h-full object-cover"
                            />
                            <VideoDataCard
                                ref={videoRef2}
                                poster={VIDEO_POSTERS[2]}
                                className="tablet:w-full aspect-[16:10] tablet:flex-1 relative w-1/2 overflow-hidden rounded-[4px] border border-white/10 bg-white/5"
                                videoClassName="w-full h-full object-cover"
                            />
                        </div>
                    </div>

                    {/* JSON Data Blocks */}
                    <div className="tablet:flex-row tablet:gap-[8px] flex w-full flex-col gap-[4px]">
                        <div className="tablet:w-1/2 tablet:h-[240px] flex h-[200px] w-full flex-col overflow-hidden rounded-[4px] border border-white/10 bg-[#1A1A1A]">
                            <div className="j-t3 flex-shrink-0 bg-[#212121] px-[16px] py-[8px] text-white">
                                robot_view_camera_params
                            </div>
                            <div
                                className="j-t3 flex-1 overflow-y-auto overflow-x-hidden whitespace-pre-wrap px-[16px] py-[8px] text-[#9B9A9D]"
                                onWheel={handleWheel}
                            >
                                {`{
  "camera_name": "Robot-View",
  "timestamp_seconds": 222.375000,

  "location": {
    "x": -2.763041,
    "y": -2.039186,
    "z": 1.500000
  },
  
  "quaternion": {
    "qw": 0.6509729028,
    "qx": 0.3388750553,
    "qy": -0.3136486709,
    "qz": -0.6025134921
  },
  
  "robot view_parameters": {
    "class_name": "RobotView Parameters",
    "extrinsic": [
      0.07720405608415604,
      -0.5718645453453064,
      0.816707193851471,
      -0.0,
      -0.9970153570175171,
      -0.04428243264555931,
      0.0632418692111969,
      0.0,
      3.725290742551124e-09,
      -0.8191520571708679,
      -0.5735765099525452,
      -0.0,
      -1.8197823762893677,
      -0.4416574239730835,
      3.245922565460205,
      1.0
    ],
    "intrinsic": {
      "height": 2160,
      "intrinsic_matrix": [
        2306.217300415039,
        0.0,
        0.0,
        0.0,
        2306.217300415039,
        0.0,
        2088.0,
        1080.0,
        1.0
      ],
      "width": 4176
    },
    "version_major": 1,
    "version_minor": 0
  },
  
  "camera_details": {
    "sensor_width_mm": 36.0,
    "sensor_height_mm": 27.0,
    "focal_length_mm": 19.881183624267578,
    "sensor_fit": "HORIZONTAL",
    "fx_pixels": 2306.217300415039,
    "fy_pixels": 2306.217300415039,
    "cx_pixels": 2088.0,
    "cy_pixels": 1080.0
  },
  
  "notes": {
    "coordinate_system": "Extrinsic matrix uses computer vision convention (Y-up, -Z forward)",
    "extrinsic_format": "Column-major 4x4 matrix (world-to-camera transformation)",
    "intrinsic_format": "Row-major 3x3 matrix [fx, 0, 0, 0, fy, 0, cx, cy, 1]",
    "quaternion_order": "WXYZ (scalar first)",
    "location_units": "Blender units (meters)"
  }
}`}
                            </div>
                        </div>

                        <div className="tablet:w-1/2 tablet:h-[240px] flex h-[200px] w-full flex-col overflow-hidden rounded-[4px] border border-white/10 bg-[#1A1A1A]">
                            <div className="j-t3 flex-shrink-0 bg-[#212121] px-[16px] py-[8px] text-white">
                                robot_view_camera_params
                            </div>
                            <div
                                className="j-t3 flex-1 overflow-y-auto overflow-x-hidden whitespace-pre-wrap px-[16px] py-[8px] text-[#9B9A9D]"
                                onWheel={handleWheel}
                            >
                                {`{
  "video_id": "CupMove.mp4",
  "task": "Robotic Pick and Place",
  "scene": "Dining room, round wooden table with magazines and decor",
  "steps": [
    {
      "start_frame": 0,
      "end_frame": 90,
      "skill": "Pick",
      "description": "The robotic gripper descends from above, aligns with the white patterned cup on the table, and closes its jaws to grasp the cup."
    },
    {
      "start_frame": 90,
      "end_frame": 180,
      "skill": "Transport",
      "description": "The robotic arm lifts the cup vertically, moves it horizontally to the left side of the table, and lowers it towards the surface."
    },
    {
      "start_frame": 180,
      "end_frame": 210,
      "skill": "Place",
      "description": "The gripper opens to release the cup onto the table surface and the arm retracts upwards to complete the task."
    }
  ]
}`}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
